// API service for backend communication
import { API_ROUTES } from '@shared/constants';
import { ApiResponse, CreateTaskResponse, Task } from '@shared/types';

let apiBaseUrl = 'http://localhost:3001';
let authToken: string | null = null;

/**
 * Initialize API service
 */
export async function initializeAPI(): Promise<void> {
  // Get configuration from environment or settings
  apiBaseUrl = getApiBaseUrl();
  authToken = getStoredAuthToken();
  
  console.log('API service initialized with base URL:', apiBaseUrl);
}

/**
 * Get API base URL from environment
 */
function getApiBaseUrl(): string {
  // This will be properly typed once Vite types are available
  return (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3001';
}

/**
 * Get stored authentication token
 */
function getStoredAuthToken(): string | null {
  try {
    return localStorage.getItem('auth_token');
  } catch {
    return null;
  }
}

/**
 * Set authentication token
 */
export function setAuthToken(token: string): void {
  authToken = token;
  try {
    localStorage.setItem('auth_token', token);
  } catch (error) {
    console.warn('Failed to store auth token:', error);
  }
}

/**
 * Clear authentication token
 */
export function clearAuthToken(): void {
  authToken = null;
  try {
    localStorage.removeItem('auth_token');
  } catch (error) {
    console.warn('Failed to remove auth token:', error);
  }
}

/**
 * Make HTTP request with retry logic
 */
async function makeRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const requestOptions: RequestInit = {
    ...options,
    headers,
  };

  try {
    const response = await fetch(`${apiBaseUrl}${url}`, requestOptions);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('API request failed:', error);
    return {
      success: false,
      error: {
        code: 'REQUEST_FAILED',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
      },
    };
  }
}

/**
 * Task API endpoints
 */
export const TasksAPI = {
  async create(taskPayload: Task): Promise<CreateTaskResponse> {
    const response = await makeRequest<Task>(API_ROUTES.TASKS, {
      method: 'POST',
      body: JSON.stringify(taskPayload),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return {
      success: response.success,
      task: response.data,
      error: response.error?.message,
    };
  },

  async getAll(): Promise<{ success: boolean; tasks?: Task[]; error?: string }> {
    const response = await makeRequest<{ success: boolean; tasks: Task[]; total: number }>(API_ROUTES.TASKS, {
      method: 'GET',
    });

    return {
      success: response.success,
      tasks: response.success ? response.data?.tasks : undefined,
      error: response.error?.message,
    };
  },

  async getById(id: string): Promise<{ success: boolean; task?: Task; error?: string }> {
    const response = await makeRequest<{ success: boolean; task: Task; error?: string }>(API_ROUTES.TASK_BY_ID(id), {
      method: 'GET',
    });

    return {
      success: response.success,
      task: response.success ? response.data?.task : undefined,
      error: response.error?.message,
    };
  },

  async update(
    id: string,
    updates: Partial<Task>
  ): Promise<{ success: boolean; task?: Task; error?: string }> {
    const response = await makeRequest<Task>(API_ROUTES.TASK_BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(updates),
    });

    return {
      success: response.success,
      task: response.data,
      error: response.error?.message,
    };
  },

  async delete(id: string): Promise<{ success: boolean; error?: string }> {
    const response = await makeRequest(API_ROUTES.TASK_BY_ID(id), {
      method: 'DELETE',
    });

    return {
      success: response.success,
      error: response.error?.message,
    };
  },
};

/**
 * Sync API endpoints
 */
export const SyncAPI = {
  async sync(data: any): Promise<{ success: boolean; data?: any; error?: string }> {
    const response = await makeRequest(API_ROUTES.SYNC, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    return {
      success: response.success,
      data: response.data,
      error: response.error?.message,
    };
  },
};

/**
 * Health check endpoint
 */
export const HealthAPI = {
  async check(): Promise<{ success: boolean; status?: string; error?: string }> {
    const response = await makeRequest<{ status: string }>(API_ROUTES.HEALTH, {
      method: 'GET',
    });

    return {
      success: response.success,
      status: response.data?.status,
      error: response.error?.message,
    };
  },
};

/**
 * Check if online and API is reachable
 * Since there's no health endpoint, we'll just check network connectivity
 */
export async function checkConnectivity(): Promise<boolean> {
  try {
    // Just check if we're online and can reach the API Gateway domain
    if (!navigator.onLine) {
      return false;
    }
    
    // Try a simple fetch to the API Gateway base URL (will return 404 but that's ok)
    const response = await fetch(apiBaseUrl, { 
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-cache'
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Upload file to backend
 */
export async function uploadFile(
  file: File,
  type: 'photo' | 'audio'
): Promise<{ success: boolean; url?: string; error?: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);

  const response = await makeRequest<{ url: string }>(API_ROUTES.MEDIA.UPLOAD, {
    method: 'POST',
    body: formData,
    headers: {}, // Don't set Content-Type for FormData
  });

  return {
    success: response.success,
    url: response.data?.url,
    error: response.error?.message,
  };
} 